import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Grid } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldTableHeader } from 'components/_basic/Form/FieldTable2/components/FieldTableHeader';
import { FieldTableContainer } from 'components/_basic/Form/FieldTableContainer';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { DividerComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/DividerComponent';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import { getEachRowValues } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetSalesForm/helpers';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { IDirectFormTableProps } from './types';
import { directFormPrefix, FIRST_COLUMN_WIDTH } from '../../../utils/form';
import { FinancingActivities } from '../Sections/FinancingActivities';
import { InvestingActivities } from '../Sections/InvestingActivities';
import { OperatingActivities } from '../Sections/OperatingActivities';
import { useGetDirectFormTotalFields } from './utils/form';

import styles from './DirectFormTable.module.scss';

export const DirectFormTable: FC<IDirectFormTableProps> = ({ disabled = false }) => {
  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const { getValues } = useFormContext();
  const TOTAL_FIELDS = useGetDirectFormTotalFields()

  const columnsNames = getPeriodColumnsNames(period);

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PROJECTED_CASH_FLOW,
  );

  const changeInCashEquivalents = getValues(
    concatWithDot(directFormPrefix, TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.name),
  );
  const cashInTheBeginning = getValues(
    concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.name),
  );
  const cashInTheEnd = getValues(concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.name));

  return (
    <FormSectionFilledCard>
      <FieldTableContainer>
        <Grid item xs className={styles.stickyTop}>
          <FieldTableHeader
            name={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
            disabled={disabled}
            columnsNames={columnsNames || []}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            editableColumn={true}
            firstColumnClass={styles.sticky}
          />
        </Grid>
        <Grid container direction="column" gap={4}>
          <Grid item xs>
            <OperatingActivities disabled={!canAccess} />
          </Grid>
          <Grid item xs>
            <InvestingActivities disabled={!canAccess} />
          </Grid>
          <Grid item xs>
            <FinancingActivities disabled={!canAccess} />
          </Grid>
          <DividerComponent />
          <Grid item xs>
            <TotalRowComponent
              name={concatWithDot(directFormPrefix, TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.name)}
              columnValues={getEachRowValues([changeInCashEquivalents], 0, period)}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.title}
              noNeedName={true}
              disabled={disabled}
              marginBottomSize="big"
            />
            <TotalRowComponent
              name={concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.name)}
              columnValues={getEachRowValues([cashInTheBeginning], 0, period, true)}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.title}
              noNeedName={true}
              disabled={disabled}
              marginBottomSize="big"
            />
            <TotalRowComponent
              name={concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.name)}
              columnValues={getEachRowValues([cashInTheEnd], 0, period)}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.title}
              noNeedName={true}
              disabled={disabled}
              marginBottomSize="small"
            />
          </Grid>
        </Grid>
      </FieldTableContainer>
    </FormSectionFilledCard>
  );
};
